import { Icon } from '@iconify/react';
import { useMediaQuery } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';

import FabButtonAnimate from 'components/animate/FabButtonAnimate';
import ChatPanel from 'components/dashboard/chat/ChatPanel';
import { APP_BAR_DESKTOP, APP_BAR_MOBILE } from 'config';

import useAuth from 'hooks/useAuth';
import { APP_ROLES, hasRole } from 'utils/roles';

import DashboardNavbar from './navbar/DashboardNavbar';
import DashboardSidebar from './navbar/DashboardSidebar';

// ----------------------------------------------------------------------

const RootStyle = styled('div')({
    display: 'flex',
    minHeight: '100%',
    overflow: 'hidden',
});

const MainStyle = styled('div')(({ theme }) => ({
    flexGrow: 1,
    overflow: 'auto',
    minHeight: '100%',
    paddingTop: APP_BAR_MOBILE + 24,
    paddingBottom: theme.spacing(10),
    [theme.breakpoints.up('lg')]: {
        paddingTop: APP_BAR_DESKTOP,
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    },
}));

// ----------------------------------------------------------------------

const DashboardLayout: React.FunctionComponent = () => {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const [chatOpen, setChatOpen] = useState(false);
    const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
    const { isAuthenticated, user, profile } = useAuth();
    // Restricted to super-admin only for now (2026-09-17, testing phase) — the
    // student-facing entitlement filtering below this point (resolveEntitlement
    // in chat.ts, cao-content-service's allowed_page_ids) is real and already
    // wired up, just not yet trusted enough to expose beyond super-admins.
    // Revert by widening this back to `isAuthenticated` once testing is done;
    // don't delete the entitlement code to "simplify" this gate.
    const canUseChat = isAuthenticated && hasRole(profile?.group, APP_ROLES.SUPER_ADMIN);

    const handleOpenWidgetClick = () => {
        setChatOpen(true);
    };

    return (
        <RootStyle>
            {isAuthenticated && isTablet && <DashboardNavbar onOpenSidebar={() => setOpen(true)} />}
            {isAuthenticated && <DashboardSidebar isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} />}
            <MainStyle
                sx={{
                    transition: theme.transitions.create('margin', {
                        duration: theme.transitions.duration.complex,
                    }),
                }}
            >
                <Outlet />
                {canUseChat && (
                    <>
                        <FabButtonAnimate
                            onClick={handleOpenWidgetClick}
                            color='secondary'
                            aria-label='chat'
                            size='large'
                            style={{ position: 'fixed', bottom: 16, right: 16 }}
                        >
                            <Icon icon='mdi:robot' width={28} height={28} />
                        </FabButtonAnimate>
                        <ChatPanel key={user?.uid} open={chatOpen} onClose={() => setChatOpen(false)} />
                    </>
                )}
            </MainStyle>
        </RootStyle>
    );
};

export default DashboardLayout;
